import { httpClient } from '@/core/api/httpClient';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import type {
  DashboardShell,
  WidgetDataDto,
  CreateWidgetPayload,
  UpdateWidgetPayload,
  ReorderWidgetsPayload,
  WidgetDef,
  WidgetType,
  DashboardScopeType,
  GroupByDimension,
  AggregationFunction,
  AggregationCondition,
} from '../types';
import { mockDashboardShell, mockWidgetDataMap } from './dashboard.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

// Bidirectional mapping between frontend snake_case and C# PascalCase WidgetType enum
const WIDGET_TYPE_TO_API: Record<WidgetType, string> = {
  counter: 'Counter',
  donut: 'Donut',
  pie: 'Pie',
  bar: 'Bar',
  horizontal_bar: 'HorizontalBar',
  stacked_bar: 'StackedBar',
  line: 'Line',
  area: 'Area',
  table: 'Table',
};

const WIDGET_TYPE_FROM_API: Record<string, WidgetType> = {
  Counter: 'counter',
  Donut: 'donut',
  Pie: 'pie',
  Bar: 'bar',
  HorizontalBar: 'horizontal_bar',
  StackedBar: 'stacked_bar',
  Line: 'line',
  Area: 'area',
  Table: 'table',
};

// The backend serializes its enums by name, and its own request parsing is
// case-insensitive — so the casing it echoes back is not something the UI can
// rely on. Every enum that reaches a `switch`, a `<Select>` option or a Zod
// enum is snapped back to the canonical frontend casing on read.
const GROUP_BY_VALUES: GroupByDimension[] = [
  'Assignee',
  'Status',
  'Priority',
  'Project',
  'Board',
  'Group',
  'Day',
  'Week',
];
const AGGREGATION_FUNCTIONS: AggregationFunction[] = [
  'count',
  'sum',
  'avg',
  'min',
  'max',
  'percent',
];
const AGGREGATION_CONDITIONS: AggregationCondition[] = ['completed', 'archived', 'has_assignee'];

function canonical<T extends string>(allowed: T[], value: unknown): T | undefined {
  if (typeof value !== 'string') return undefined;
  return allowed.find((a) => a.toLowerCase() === value.toLowerCase());
}

function normalizeWidgetDef(widget: WidgetDef & { type: string }): WidgetDef {
  const { aggregation } = widget.config;
  return {
    ...widget,
    type: WIDGET_TYPE_FROM_API[widget.type] ?? (widget.type as WidgetType),
    config: {
      ...widget.config,
      groupBy: canonical(GROUP_BY_VALUES, widget.config.groupBy),
      ...(aggregation
        ? {
            aggregation: {
              ...aggregation,
              function: canonical(AGGREGATION_FUNCTIONS, aggregation.function) ?? 'count',
              condition: canonical(AGGREGATION_CONDITIONS, aggregation.condition),
            },
          }
        : {}),
    },
  };
}

// The backend names this shape `seriesMulti`; the app's discriminated union
// uses snake_case like every other shape.
function normalizeWidgetData(data: unknown): WidgetDataDto {
  const dto = data as Record<string, unknown>;
  return (dto.shape === 'seriesMulti'
    ? { ...dto, shape: 'series_multi' }
    : dto) as unknown as WidgetDataDto;
}

function normalizeShell(
  shell: DashboardShell & { widgets: Array<WidgetDef & { type: string }> },
): DashboardShell {
  return { ...shell, widgets: shell.widgets.map(normalizeWidgetDef) };
}

function scopeBase(scopeType: DashboardScopeType, scopeId: string): string {
  if (scopeType === 'tenant') return `${API_V1}/tenants/dashboard`;
  if (scopeType === 'board') return `${API_V1}/boards/${scopeId}/dashboard`;
  return `${API_V1}/projects/${scopeId}/dashboard`;
}

export const dashboardService = {
  getShell(scopeType: DashboardScopeType, scopeId: string, signal?: AbortSignal) {
    if (USE_MOCK) return Promise.resolve({ data: mockDashboardShell, status: 200 });
    return (
      signal
        ? httpClient.get<DashboardShell & { widgets: Array<WidgetDef & { type: string }> }>(
            scopeBase(scopeType, scopeId),
            { signal },
          )
        : httpClient.get<DashboardShell & { widgets: Array<WidgetDef & { type: string }> }>(
            scopeBase(scopeType, scopeId),
          )
    ).then((res) => ({ ...res, data: normalizeShell(res.data) }));
  },

  getWidgetData(
    scopeType: DashboardScopeType,
    scopeId: string,
    widgetId: string,
    signal?: AbortSignal,
  ) {
    if (USE_MOCK) {
      const data = mockWidgetDataMap[widgetId];
      if (data) return Promise.resolve({ data, status: 200 });
      return Promise.reject(new Error(`No mock data for widget ${widgetId}`));
    }
    const url = `${scopeBase(scopeType, scopeId)}/widgets/${widgetId}/data`;
    return (
      signal
        ? httpClient.get<WidgetDataDto & { shape: string }>(url, { signal })
        : httpClient.get<WidgetDataDto & { shape: string }>(url)
    ).then((res) => ({ ...res, data: normalizeWidgetData(res.data) }));
  },

  createWidget(scopeType: DashboardScopeType, scopeId: string, payload: CreateWidgetPayload) {
    // type is sent in PascalCase; backend responds with only the new widget id.
    const apiPayload = { ...payload, type: WIDGET_TYPE_TO_API[payload.type] };
    return httpClient.post<typeof apiPayload, { id: string }>(
      `${scopeBase(scopeType, scopeId)}/widgets`,
      apiPayload,
    );
  },

  updateWidget(
    scopeType: DashboardScopeType,
    scopeId: string,
    widgetId: string,
    payload: UpdateWidgetPayload,
  ) {
    // Backend returns an empty 200 body on success.
    return httpClient.put<UpdateWidgetPayload, void>(
      `${scopeBase(scopeType, scopeId)}/widgets/${widgetId}`,
      payload,
    );
  },

  deleteWidget(scopeType: DashboardScopeType, scopeId: string, widgetId: string) {
    if (USE_MOCK) return Promise.resolve({ data: undefined as unknown, status: 204 });
    return httpClient.delete(`${scopeBase(scopeType, scopeId)}/widgets/${widgetId}`);
  },

  reorderWidgets(scopeType: DashboardScopeType, scopeId: string, payload: ReorderWidgetsPayload) {
    if (USE_MOCK) return Promise.resolve({ data: undefined as unknown, status: 200 });
    return httpClient.patch(`${scopeBase(scopeType, scopeId)}/widgets/reorder`, payload);
  },
};
